import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ScopedSummarySection } from 'src/charts/SummarySection'
import { Skeleton } from 'src/components/Skeleton'
import { StatusModal } from 'src/components/StatusModal'
import {
  AI_LISTENING_BRAND_NEW_ROUTE,
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
} from 'src/constants/routes'
import {
  useAiListeningDateRange,
  useAiListeningLink,
} from 'src/hooks/useAiListeningDateRange'
import { useDeleteAiListeningProfileMutation } from 'src/reactQuery/useDeleteAiListeningProfileMutation'
import { useGetAiListeningProfilePromptsQuery } from 'src/reactQuery/useGetAiListeningProfilePromptsQuery'
import { useGetAiListeningProfileQuery } from 'src/reactQuery/useGetAiListeningProfileQuery'
import { useGetAiListeningProfilesQuery } from 'src/reactQuery/useGetAiListeningProfilesQuery'
import styles from './brandProfile.module.scss'
import { AddPromptFlow } from '../shared/AddPrompt'
import { BrandDashboardView } from '../shared/Dashboard'
import { SiteAuditCard } from '../shared/SiteAudit'
import { rememberBrandProfile } from '../shared/hooks'
import { AiListeningPage, PageBreadcrumb, PageTitleRow } from '../shared/Layout'
import { ProfileHeader, ProfileTabs } from '../shared/ProfileHeader'
import { PromptList } from '../shared/PromptList'
import { StateCard } from '../shared/StateCard'

type DeleteStep = 'confirm' | 'failed' | 'idle' | 'success'

// /ai-listening/brand/:profileId — "Your Brand's Profile": the dashboard and
// tracked prompts once there is data, otherwise the empty / processing state.
export const BrandProfilePage: FC = () => {
  const { profileId = '' } = useParams<{ profileId: string }>()
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const range = useAiListeningDateRange()
  const [isAddingPrompt, setIsAddingPrompt] = useState(false)
  const [deleteStep, setDeleteStep] = useState<DeleteStep>('idle')

  const {
    data: profile,
    isError,
    isLoading,
  } = useGetAiListeningProfileQuery(profileId)
  const { data: profiles = [] } = useGetAiListeningProfilesQuery()
  const isReady = profile?.state === 'ready'
  const { data: promptData, isLoading: isPromptsLoading } =
    useGetAiListeningProfilePromptsQuery(isReady ? profileId : undefined, {
      status: 'active',
    })

  useEffect(() => {
    if (profile) rememberBrandProfile(profile._id)
  }, [profile?._id])

  const deleteMutation = useDeleteAiListeningProfileMutation({
    onError: () => setDeleteStep('failed'),
    onSuccess: () => setDeleteStep('success'),
  })

  const leaveDeletedProfile = useCallback(() => {
    setDeleteStep('idle')
    navigate(link(AI_LISTENING_BRAND_ROUTE), { replace: true })
  }, [link, navigate])

  const deleteProfile = () => {
    if (!deleteMutation.isPending) deleteMutation.mutate(profileId)
  }

  const breadcrumb = (
    <PageBreadcrumb
      fallbackTo={link(AI_LISTENING_BRAND_ROUTE)}
      items={['AI Listening', 'Brand Profiles']}
    />
  )

  if (isLoading) {
    return (
      <AiListeningPage>
        {breadcrumb}
        <Skeleton height={52} radius={10} width={420} />
        <Skeleton height={400} radius={20} width="100%" />
      </AiListeningPage>
    )
  }

  // The success modal outlives the profile it deleted.
  if ((isError || !profile) && deleteStep !== 'success') {
    return (
      <AiListeningPage>
        {breadcrumb}
        <PageTitleRow title="Your Brand AI Visibility">
          <ProfileTabs active="brand" />
        </PageTitleRow>
        <StateCard
          action={{
            label: 'Go to Brand Profile',
            onClick: () => navigate(link(AI_LISTENING_BRAND_ROUTE)),
          }}
          description="This brand profile doesn't exist or you no longer have access to it."
          icon="brand"
          title="Brand profile not found"
        />
      </AiListeningPage>
    )
  }

  const brandProfiles = profiles.filter((item) => item.role !== 'competitor')
  const prompts = promptData?.prompts ?? []

  return (
    <AiListeningPage>
      {breadcrumb}
      <PageTitleRow title="Brand Profile" />

      {profile && (
        <ProfileHeader
          onRemove={() => setDeleteStep('confirm')}
          profile={profile}
          right={<ProfileTabs active="brand" profileId={profile._id} />}
          switcher={{
            activeId: profile._id,
            onAdd: () => navigate(link(AI_LISTENING_BRAND_NEW_ROUTE)),
            onSelect: (id) =>
              navigate(
                link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId: id })
              ),
            options: brandProfiles.map((item) => ({
              id: item._id,
              label: item.name,
            })),
          }}
        />
      )}

      {profile?.state === 'processing' && (
        <StateCard
          description={
            <>
              Hapttic is analyzing how your brand appears across ChatGPT,
              Gemini, Perplexity,
              <br />
              and Claude. Your AI visibility insights will be available soon.
            </>
          }
          icon="processing"
          title="Your Brand Profile Is Being Analyzed"
        />
      )}

      {profile?.state === 'empty' && (
        <StateCard
          action={{
            label: 'Add Prompt',
            onClick: () => setIsAddingPrompt(true),
          }}
          description={
            <>
              Prompts are the questions your customers ask AI assistants.
              <br />
              Add your first one to start measuring how {profile.brandName}{' '}
              shows up in the answers.
            </>
          }
          icon="prompt"
          title="Add your first prompt"
        />
      )}

      {profile && isReady && (
        <div className={styles.readyBody}>
          <BrandDashboardView
            profileId={profile._id}
            range={range}
            subjectId={profile._id}
          />
          <PromptList
            isLoading={isPromptsLoading}
            onAddPrompt={() => setIsAddingPrompt(true)}
            prompts={prompts}
          />
          <ScopedSummarySection
            scope={{
              id: profile._id,
              kind: 'aiListeningProfile',
              subject: profile._id,
            }}
          />
        </div>
      )}

      {profile && (
        <SiteAuditCard
          brandName={profile.brandName}
          profileId={profile._id}
          subjectId={profile._id}
        />
      )}

      {profile && (
        <AddPromptFlow
          isOpen={isAddingPrompt}
          onClose={() => setIsAddingPrompt(false)}
          profile={profile}
        />
      )}

      <StatusModal
        doneLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete Brand'}
        hideCountdown
        isOpen={deleteStep === 'confirm'}
        message={`This will permanently delete ${profile?.name ?? 'this brand profile'} and remove all associated data, settings, and performance history. This action cannot be undone.`}
        onClose={() => setDeleteStep('idle')}
        onSecondaryAction={deleteProfile}
        secondaryLabel="Cancel"
        title={`Remove ${profile?.name ?? 'brand profile'}?`}
        type="delete"
      />
      <StatusModal
        isOpen={deleteStep === 'success'}
        message="The brand profile and its historical data were successfully deleted."
        onClose={leaveDeletedProfile}
        title="Brand Deleted Successfully"
        type="success"
      />
      <StatusModal
        doneLabel={deleteMutation.isPending ? 'Trying…' : 'Try Again'}
        hideCountdown
        isOpen={deleteStep === 'failed'}
        message="Something went wrong while removing. Please try again."
        onClose={() => setDeleteStep('idle')}
        onSecondaryAction={deleteProfile}
        secondaryLabel="Discard"
        title="Failed to Delete Brand"
        type="error"
      />
    </AiListeningPage>
  )
}
